/**
 * Date utility for auditing pickup games & hosted runs.
 * Evaluates game date strings against current system date (2026-08-08 Saturday).
 */

export interface GameDateInfo {
  isPast: boolean;
  isToday: boolean;
  isTomorrow: boolean;
  formattedLabel: string;
  badgeLabel: string;
  badgeStyle: string;
}

export const CURRENT_REF_DATE = new Date('2026-08-08T12:00:00-07:00'); // Saturday, Aug 8, 2026

export function getGameDateInfo(dateTimeStr: string): GameDateInfo {
  if (!dateTimeStr) {
    return {
      isPast: false,
      isToday: true,
      isTomorrow: false,
      formattedLabel: 'Today',
      badgeLabel: 'Upcoming Run',
      badgeStyle: 'bg-lime-400 text-black',
    };
  }

  const lower = dateTimeStr.toLowerCase();

  // Explicit past indicator strings
  if (lower.includes('past') || lower.includes('completed') || lower.includes('finished')) {
    return {
      isPast: true,
      isToday: false,
      isTomorrow: false,
      formattedLabel: dateTimeStr,
      badgeLabel: 'Past Game • Completed',
      badgeStyle: 'bg-slate-700/80 text-amber-300 border border-amber-500/30',
    };
  }

  // Check if string contains "friday" when reference date is Saturday (Aug 8)
  if (lower.includes('friday') && !lower.includes('next friday')) {
    return {
      isPast: true,
      isToday: false,
      isTomorrow: false,
      formattedLabel: 'Past Friday (Aug 7) • Completed',
      badgeLabel: 'Past Game • Completed',
      badgeStyle: 'bg-slate-700/80 text-amber-300 border border-amber-500/30',
    };
  }

  // Standard relative text matches
  if (lower.includes('today')) {
    return {
      isPast: false,
      isToday: true,
      isTomorrow: false,
      formattedLabel: dateTimeStr,
      badgeLabel: "Today's Run 🔥",
      badgeStyle: 'bg-lime-400 text-black font-extrabold',
    };
  }

  if (lower.includes('tomorrow')) {
    return {
      isPast: false,
      isToday: false,
      isTomorrow: true,
      formattedLabel: dateTimeStr,
      badgeLabel: 'Tomorrow ⚡',
      badgeStyle: 'bg-indigo-600 text-white font-extrabold border border-indigo-400',
    };
  }

  // Attempt standard ISO / Date parsing
  const parsed = new Date(dateTimeStr);
  if (!isNaN(parsed.getTime())) {
    const startOfToday = new Date('2026-08-08T00:00:00-07:00').getTime();
    const startOfGameDay = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()).getTime();

    if (startOfGameDay < startOfToday) {
      return {
        isPast: true,
        isToday: false,
        isTomorrow: false,
        formattedLabel: `${parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} • Completed`,
        badgeLabel: 'Past Game • Completed',
        badgeStyle: 'bg-slate-700/80 text-amber-300 border border-amber-500/30',
      };
    } else if (startOfGameDay === startOfToday) {
      return {
        isPast: false,
        isToday: true,
        isTomorrow: false,
        formattedLabel: `Today at ${parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`,
        badgeLabel: "Today's Run 🔥",
        badgeStyle: 'bg-lime-400 text-black font-extrabold',
      };
    } else {
      return {
        isPast: false,
        isToday: false,
        isTomorrow: false,
        formattedLabel: dateTimeStr,
        badgeLabel: 'Scheduled Run',
        badgeStyle: 'bg-cyan-500 text-black font-extrabold',
      };
    }
  }

  return {
    isPast: false,
    isToday: lower.includes('saturday'),
    isTomorrow: lower.includes('sunday'),
    formattedLabel: dateTimeStr,
    badgeLabel: 'Scheduled Run',
    badgeStyle: 'bg-indigo-700 text-indigo-100',
  };
}
